<script setup>
// 2026 신규 페이지: "가족 구성원이 함께 참여하는 AI·디지털 리터러시 가정연계 학습 제안"
// 문구: 엑셀 '가정연계자료' 시트(학교급별 제시문/학생/학부모/참고자료/카드 링크), 카드 제목·설명: 시안 PDF(재수정본)
import { computed } from 'vue';
import { FAMILY_RESOURCES, PRINCIPAL_LAST_PAGE } from './data/reportData';
import { toSchLev, schoolTypeLabel } from './data/reportLogic';
import guideImg from '@/assets/images/result/ai/2026ai/family_guide_v2.png';
import card01 from '@/assets/images/result/ai/2026ai/family_card_01_v2.png';
import card02 from '@/assets/images/result/ai/2026ai/family_card_02_v2.png';
import card03 from '@/assets/images/result/ai/2026ai/family_card_03_v2.png';
import card04 from '@/assets/images/result/ai/2026ai/family_card_04_v2.png';
import iconInfo from '@/assets/images/result/ai/icon_Info.png';
import iconStudent from '@/assets/images/result/ai/icon_student.png';

const props = defineProps({
  schulType: { type: String, default: 'E' } // 'E' | 'M' | 'H'
});

const lev = computed(() => toSchLev(props.schulType));
const schoolLabel = computed(() => schoolTypeLabel(props.schulType));

const intro = computed(() => FAMILY_RESOURCES.intro[lev.value] || '');
const studentText = computed(() => FAMILY_RESOURCES.student[lev.value] || '');
const parentText = computed(() => FAMILY_RESOURCES.parent[lev.value] || '');
const reference = FAMILY_RESOURCES.reference || { text: '', url: '' };
const referenceLinkLabel = 'AI·에듀테크 공교육 도입 및 활용 가이드라인(학부모용)';

// 카드 제목/설명은 시안 PDF 기준. 링크는 엑셀 시트의 key로 매칭.
const CARD_META = {
  'AI·에듀테크': { title: 'AI·에듀테크, 가정에서<br>어떻게 함께할까요', image: card01,
    desc: '자녀가 AI·에듀테크를 올바르게 활용할 수 있도록 초·중·고 학교급별로 보호자가 실천할 수 있는 지도 방법과 활용 원칙을 안내합니다.' },
  '디벗': { title: '디벗 학부모가이드', image: card02,
    desc: '자녀가 디벗을 안전하고 책임감 있게 활용할 수 있도록 기기·계정 관리 방법과 디지털 시민 에티켓을 안내합니다. 가정에서 함께 실천할 수 있는 디지털 생활 약속과 체크리스트를 제공합니다.' },
  'AI 교육': { title: 'AI 교육 브리핑', image: card03,
    desc: '자녀의 AI·디지털 역량 성장에 대한 관심과 참여를 높일 수 있도록, 서울특별시교육청의 AI·디지털 교육 관련 최신 소식과 다양한 교육 자료를 제공합니다.' },
  '디지털': { title: '디지털 시민성 교육자료<br>(가정용 활동지)', image: card04,
    desc: '자녀가 디지털 시민성을 익힐 수 있도록 학교급별 활동지를 제공합니다.' },
};

const cards = computed(() =>
  (FAMILY_RESOURCES.cards || []).map(c => ({
    key: c.key,
    url: c.url,
    title: CARD_META[c.key]?.title || c.key,
    image: CARD_META[c.key]?.image || null,
    desc: CARD_META[c.key]?.desc || c.text || ''
  }))
);

// 학교장용 결과지 마지막 페이지(SchoolPage.vue)의 활용 안내 표 중 '학생·가정' 한 칸만 그대로 가져온다.
const studentUsage = computed(() =>
  (PRINCIPAL_LAST_PAGE.usage || []).find(u => u.target === '학생·가정') || null
);
</script>

<template>
  <div class="report-inner family-page">
    <div class="card-wrap">
      <!-- 학교장용 결과지에 있는 '학생·가정' 활용 안내 표 (그 표 전체가 아니라 학생·가정 칸만) -->
      <div class="card heatmap" v-if="studentUsage">
        <h5 class="card-title">「AI‧디지털 리터러시 진단검사」 결과, 이렇게 활용해요.</h5>
      </div>
      <div class="ai-table primary student-section mb-3" v-if="studentUsage">
        <div class="row">
          <div class="th col">
            <div class="th">
              <img :src="iconStudent" :alt="studentUsage.target">
              <span>{{ studentUsage.target }}</span>
            </div>
            <div class="th">
              <p v-for="item in studentUsage.items" :key="item.area">{{ item.area }}</p>
            </div>
          </div>
          <div class="td">
            <div class="td-content">
              <ul>
                <li v-for="item in studentUsage.items" :key="item.area">{{ item.method }}</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <p class="family-intro">
        <img :src="iconInfo" alt="안내" class="info-icon">
        <span>{{ intro }}</span>
      </p>

      <div class="card">
        <h5 class="card-title">{{ schoolLabel }} 시기의 AI·디지털 리터러시 함양</h5>
        <div class="ai-table primary mt-2 family-table">
          <div class="row">
            <div class="th">학생</div>
            <div class="td">{{ studentText }}</div>
          </div>
          <div class="row">
            <div class="th">학부모</div>
            <div class="td">{{ parentText }}</div>
          </div>
          <div class="row">
            <div class="th">참고자료</div>
            <div class="td reference">
              <div class="text">
                <p>{{ reference.text }}</p>
                <a :href="reference.url" target="_blank" class="blue-link">{{ referenceLinkLabel }}</a>
              </div>
              <a :href="reference.url" target="_blank" class="guide-img">
                <img :src="guideImg" alt="AI·에듀테크 공교육 도입 및 활용 가이드라인(학부모용) 표지">
              </a>
            </div>
          </div>
        </div>
      </div>

      <div class="card mt-4">
        <h5 class="card-title">가족 구성원이 함께 참여하는 AI·디지털 리터러시 가정연계 학습 제안</h5>
        <div class="family-cards mt-3">
          <div class="family-card" v-for="card in cards" :key="card.key">
            <p class="title" v-html="card.title"></p>
            <div class="thumb">
              <img v-if="card.image" :src="card.image" :alt="card.key">
            </div>
            <p class="desc">{{ card.desc }}</p>
            <a :href="card.url" target="_blank" class="go-link">바로가기 <span class="arrow">&gt;</span></a>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
// SchoolPage.vue(학교장용 마지막 페이지)의 '이렇게 활용해요' 표와 동일한 스타일.
// scoped 스타일이라 컴포넌트 간에 공유가 안 돼서 그대로 옮겨왔다.
.ai-table {
  .row div.th.col {
    padding: 0;
    display: flex;
    &::before { display: none; }
    .th {
      position: relative;
      padding: 1rem 0 1rem 1rem;
      display: flex;
      align-items: center;
      justify-content: center;
      min-width: auto;
      height: 100%;
      &::before {
        content: "";
        position: absolute;
        background-color: var(--result-ai-primary-100);
        top: 50%;
        right: 0;
        transform: translateY(-50%);
        width: 1px;
        height: calc(100% - 10px);
      }
      &:nth-child(1) {
        font-weight: 600;
        text-align: center;
        width: 150px;
        flex-direction: column;
        gap: 0.5rem;
        img { width: 40px; height: 40px; }
        span { font-size: 1rem; }
      }
      &:nth-child(2) {
        display: flex;
        flex-direction: column;
        align-items: center;
        width: 180px;
        p { font-weight: 600; color: var(--result-ai-primary-800); font-size: 19px; }
      }
    }
  }
  .td {
    padding: 1rem .5rem 1rem 1rem !important;
    line-height: 1.5;
    text-align: justify;
    word-break: keep-all;
    text-justify: inter-word;
  }
  &.student-section .row {
    .th {
      &:nth-child(2) {
        padding: 0 !important;
        &::before { display: none; }
      }
      p {
        position: relative;
        width: 100%;
        flex: 1;
        display: flex;
        align-items: center;
        justify-content: center;
        &::before {
          content: "";
          position: absolute;
          background-color: var(--result-ai-primary-100);
          top: 50%;
          right: 0;
          transform: translateY(-50%);
          width: 1px;
          height: calc(100% - 10px);
        }
        &::after {
          content: "";
          position: absolute;
          background-color: var(--result-ai-primary-100);
          right: 0;
          bottom: 0;
          width: calc(100% - 10px);
          height: 1px;
        }
        &:last-child::after { display: none; }
      }
    }
    .td {
      display: flex;
      padding: 0 !important;
      height: 223px; // 학생·가정 4줄 기준 (SchoolPage.vue와 동일)
      .td-content {
        ul {
          height: 100%;
          display: flex;
          flex-direction: column;
          text-align: left;
          width: 100%;
          li {
            flex: 1;
            display: flex;
            align-items: flex-start;
            justify-content: center;
            flex-direction: column;
            padding: .2rem 0.8rem;
            position: relative;
            line-height: 1.3;
            width: 100%;
            text-align: justify;
            &::after {
              content: "";
              position: absolute;
              background-color: var(--result-ai-primary-100);
              left: 0;
              bottom: 0;
              width: calc(100% - 10px);
              height: 1px;
            }
            &:last-child::after { display: none; }
          }
        }
      }
    }
  }
}

.family-page {
  .family-intro {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding: 4px 0 14px;
    font-size: 19px;
    line-height: 1.6;
    word-break: keep-all;
    .info-icon { width: 18px; height: 18px; margin-top: 4px; flex: none; }
  }
  .family-table {
    .th { width: 130px; min-width: 130px; justify-content: center; font-weight: 600; }
    .td { line-height: 1.7; word-break: keep-all; }
    .td.reference {
      display: flex;
      align-items: center;
      gap: 16px;
      .text { flex: 1; }
      .blue-link { display: inline-block; margin-top: 8px; text-decoration: underline; }
      .guide-img {
        flex: none;
        width: 110px;
        img { width: 100%; height: auto; display: block; border: 1px solid #e3e6ef; border-radius: 4px; }
      }
    }
  }
  .family-cards {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 14px;
  }
  .family-card {
    display: flex;
    flex-direction: column;
    align-items: center;
    border: 1px solid var(--result-ai-border, #C7C9D5);
    border-radius: 10px;
    padding: 14px 12px 16px;
    background: #fff;
    .title {
      width: 100%;
      min-height: 46px;
      display: flex;
      align-items: center;
      justify-content: center;
      text-align: center;
      font-weight: 700;
      font-size: 14px;
      line-height: 1.4;
      background: var(--result-ai-primary-50, #EAF6FE);
      border-radius: 20px;
      padding: 4px 8px;
      margin-bottom: 12px;
      word-break: keep-all;
    }
    .thumb {
      width: 100%;
      height: 220px;
      display: flex;
      align-items: center;
      justify-content: center;
      overflow: hidden;
      img { max-height: 100%; max-width: 100%; object-fit: contain; }
    }
    .desc {
      flex: 1;
      margin-top: 12px;
      font-size: 14px;
      line-height: 1.7;
      // 카드 폭이 좁아 어절 단위로 두면 여백이 크게 남는다 → 글자 단위로 끊는다
      word-break: break-all;
      text-align: justify;
    }
  }
  .go-link {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    margin-top: 14px;
    padding: 3px 12px;
    border: 1px solid var(--result-ai-primary-800, #566391);
    border-radius: 6px;
    background: #fff;
    color: var(--result-ai-primary-800, #566391);
    font-size: 12px;
    font-weight: 600;
    text-decoration: none;
    white-space: nowrap;
    .arrow { font-size: 11px; }
  }
}
</style>
